(() => {
  if (typeof window === 'undefined') return;

  // Avoid double-install (e.g., HMR)
  if (window.__CONSOLE_CAPTURE_INSTALLED__) return;
  window.__CONSOLE_CAPTURE_INSTALLED__ = true;

  const originals = {
    log: console.log.bind(console),
    info: console.info.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
    debug: console.debug.bind(console),
  };

  const buffer = [];
  const MAX = 5000; // keep last N entries

  function safeJson(value) {
    try {
      const seen = new WeakSet();
      return JSON.stringify(value, (_k, v) => {
        if (typeof v === 'bigint') return v.toString() + 'n';
        if (typeof v === 'function')
          return `[Function${v.name ? `: ${v.name}` : ''}]`;
        if (typeof v === 'symbol') return v.toString();
        if (v && typeof v === 'object') {
          if (seen.has(v)) return '[Circular]';
          seen.add(v);
        }
        return v;
      });
    } catch {
      return null;
    }
  }

  function toArgString(a) {
    if (a instanceof Error) {
      const head = `${a.name}: ${a.message}`;
      return a.stack ? `${head}\n${a.stack}` : head;
    }
    if (typeof a === 'string') return a;
    if (a == null || typeof a === 'number' || typeof a === 'boolean')
      return String(a);

    // Heuristics: avoid touching Vue component public instances & reactivity proxies.
    const isLikelyVueComponent = (() => {
      try {
        return (
          !!a &&
          typeof a === 'object' &&
          '$' in a && // Vue public instance namespace
          ('$data' in a || '$props' in a || '$el' in a)
        );
      } catch {
        return false;
      }
    })();
    const isLikelyVueReactive = (() => {
      try {
        return (
          !!a &&
          typeof a === 'object' &&
          ('__v_raw' in a ||
            '__v_isReactive' in a ||
            '__v_isReadonly' in a ||
            '__v_isRef' in a)
        );
      } catch {
        return false;
      }
    })();
    if (isLikelyVueComponent || isLikelyVueReactive) {
      return `[${isLikelyVueComponent ? 'VueComponent' : 'VueReactive'}]`;
    }

    // Only try to JSON-encode plain objects/arrays; otherwise, print a safe tag.
    const tag = Object.prototype.toString.call(a); // no property access / enumeration
    if (tag === '[object Object]' || tag === '[object Array]') {
      const json = safeJson(a); // cycle-safe
      if (json !== null) return json;
    }
    return tag;
  }

  function pushLine(line) {
    buffer.push(line);
    if (buffer.length > MAX) buffer.splice(0, buffer.length - MAX);
  }

  function wrap(level) {
    return (...args) => {
      const ts = new Date().toISOString();
      const head = `[${ts}] ${level.toUpperCase()}`;
      const parts = args.map(toArgString);
      const msg = parts.join(' ');
      // capture a lightweight stack (skip the first 2 lines: wrapper + Error ctor)
      const stack = new Error().stack;
      const extraStack = stack ? stack.split('\n').slice(2).join('\n') : '';
      const line = extraStack
        ? `${head} ${msg}\n${extraStack}`
        : `${head} ${msg}`;

      pushLine(line);
      originals[level](...args); // still output to the real console
    };
  }

  console.log = wrap('log');
  console.info = wrap('info');
  console.warn = wrap('warn');
  console.error = wrap('error');
  console.debug = wrap('debug');

  // Also capture uncaught errors & unhandled rejections
  window.addEventListener('error', (e) => {
    // Route through console.* so it flows into the same buffer
    console.error(
      '[UncaughtError]',
      e.message,
      `${e.filename}:${e.lineno}:${e.colno}`,
      e.error
    );
  });

  window.addEventListener('unhandledrejection', (e) => {
    console.error('[UnhandledRejection]', e.reason);
  });

  function getTxt() {
    return buffer.join('\n');
  }

  function downloadTxt(filename) {
    const name =
      filename ||
      `console-${location.hostname}-${new Date().toISOString().replace(/[:.]/g, '-')}.txt`;
    const blob = new Blob([getTxt()], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = name;
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(a.href);
    a.remove();
  }

  window.dumpConsoleTxt = () => downloadTxt();
})();
